import axios from 'axios';

// For debugging network connectivity issues
const debugRequest = (config) => {
  console.log(`📡 Request: ${config.method.toUpperCase()} ${config.url}`);
  return config;
};

const debugResponse = (response) => {
  console.log(`📡 Response: ${response.status} ${response.statusText} from ${response.config.url}`);
  return response;
};

const debugError = (error) => {
  console.error(`📡 Error: ${error.message}`);
  return Promise.reject(error);
};

// Get the port from the run.py script (default is 8001)
const API_URL = process.env.REACT_APP_API_URL || 'https://deva-survey-api.promptcatalog.online';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // No timeout - let requests run as long as needed
  // timeout: 30000
});

// Add request/response interceptors for debugging
api.interceptors.request.use(debugRequest, debugError);
api.interceptors.response.use(debugResponse, debugError);

// Survey API
export const getSurveys = async () => {
  const response = await api.get('/surveys');
  return response.data;
};

export const getSurvey = async (surveyId) => {
  const response = await api.get(`/surveys/${surveyId}`);
  return response.data;
};

export const createSurvey = async (surveyData) => {
  const response = await api.post('/surveys', surveyData);
  return response.data;
};

export const updateSurvey = async (surveyId, surveyData) => {
  const response = await api.put(`/surveys/${surveyId}`, surveyData);
  return response.data;
};

export const deleteSurvey = async (surveyId) => {
  const response = await api.delete(`/surveys/${surveyId}`);
  return response.data;
};

export const importSurvey = async (file) => {
  try {
    console.log(`API call: Importing survey from file ${file.name} (${(file.size / 1024).toFixed(2)}KB)`);
    
    // Check if the file is valid
    if (!file || file.size === 0) {
      throw new Error('Invalid or empty file provided for import');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Log FormData for debugging
    for (let [key, value] of formData.entries()) {
      console.log(`FormData entry: ${key} = ${value instanceof File ? `File: ${value.name}, Size: ${value.size} bytes` : value}`);
    }
    
    // Super verbose debugging
    console.log(`🚀 Preparing to import survey from file: ${file.name}`);
    console.log(`🔍 API base URL: ${API_URL}`);
    console.log(`🔍 File info: ${file.name}, ${file.type}, ${file.size} bytes`);
    
    // Try with fetch API first (more CORS-friendly)
    console.log(`🔍 Trying fetch API first (more CORS-friendly)`);
    
    try {
      const fetchResponse = await fetch(`${API_URL}/surveys/import`, {
        method: 'POST',
        body: formData,
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-cache',
        redirect: 'follow',
        headers: {
          'X-Debug-Request': 'import-survey-fetch',
          'Accept': 'application/json'
          // Note: Don't set Content-Type here, let the browser set it with boundary
        }
      });
      
      console.log(`Fetch API status: ${fetchResponse.status}`);
      
      if (fetchResponse.ok) {
        const data = await fetchResponse.json();
        console.log(`✅ Survey import successful with Fetch API:`, data);
        return data.survey || data;
      } else {
        // Try to get error message
        try {
          const errorData = await fetchResponse.json();
          console.error(`Fetch API error: ${fetchResponse.status}`, errorData);
        } catch (e) {
          console.error(`Fetch API error: ${fetchResponse.status}, couldn't parse error response`);
        }
        
        // We'll throw later and try with axios as fallback
        console.log(`Fetch API failed, trying Axios as fallback...`);
      }
    } catch (fetchError) {
      console.error(`Fetch API failed:`, fetchError);
      console.log(`Falling back to Axios for the request...`);
    }
    
    // Fallback to axios with special config
    const config = {
      headers: {
        // Let axios set the right multipart/form-data content type with boundary
        'X-Debug-Request': 'import-survey-axios-fallback',
        'Accept': 'application/json'
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${percentCompleted}%`);
      },
      // Increase timeout for processing
      timeout: 10 * 60 * 1000, // 10 minutes
      withCredentials: false, // Don't send cookies
      maxRedirects: 5,
      maxContentLength: 100 * 1024 * 1024, // 100MB
      maxBodyLength: 100 * 1024 * 1024, // 100MB
    };
    
    console.log(`🚀 Sending import request with Axios to ${API_URL}/surveys/import`);
    const response = await axios.post(`${API_URL}/surveys/import`, formData, config);
    
    if (!response || !response.data) {
      console.error('Empty response or missing data from import survey API call');
      throw new Error('Invalid response from server');
    }
    
    console.log(`✅ Survey import successful with Axios: ${response.data.id || response.data.survey?.id}`);
    return response.data.survey || response.data;
  } catch (error) {
    console.error('❌ API call failed: Error importing survey', error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    } else if (error.request) {
      console.error('No response received from server. This could be due to:');
      console.error('- Network connectivity issues');
      console.error('- CORS restrictions');
      console.error('- Server timeout');
      console.error('- Invalid server response');
    } else {
      console.error('Error setting up request:', error.message);
    }
    
    // Create a last-ditch XHR request (lowest level, most CORS-friendly)
    console.log(`🔥 LAST RESORT: Trying XMLHttpRequest directly`);
    
    // Create a new FormData for XHR request to avoid scope issues
    const xhrFormData = new FormData();
    xhrFormData.append('file', file);
    
    // Return a new promise for the XHR
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        
        // Setup event listeners
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            console.log(`XHR Upload: ${percentComplete}%`);
          }
        };
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log(`✅ XHR request successful, status: ${xhr.status}`);
            try {
              const data = JSON.parse(xhr.responseText);
              console.log(`Successfully parsed XHR response:`, data);
              resolve(data.survey || data);
            } catch (e) {
              console.error(`Error parsing XHR response:`, e);
              reject(new Error('Invalid JSON response from server'));
            }
          } else {
            console.error(`XHR request failed with status: ${xhr.status}`);
            reject(new Error(`Server returned status ${xhr.status}`));
          }
        };
        
        xhr.onerror = function() {
          console.error(`XHR network error occurred`);
          reject(new Error('Network error occurred during upload'));
        };
        
        xhr.ontimeout = function() {
          console.error(`XHR request timed out`);
          reject(new Error('Request timed out'));
        };
        
        // Open and send the request
        xhr.open('POST', `${API_URL}/surveys/import`, true);
        xhr.setRequestHeader('X-Debug-Request', 'import-survey-xhr-last-resort');
        xhr.timeout = 15 * 60 * 1000; // 15 minutes
        
        xhr.send(xhrFormData);
        
        console.log(`XHR request sent to ${API_URL}/surveys/import`);
      } catch (xhrError) {
        console.error(`Failed to create XHR request:`, xhrError);
        reject(new Error('Failed to setup XHR request: ' + xhrError.message));
      }
    }).catch(finalError => {
      // Add more context to the error
      const enhancedError = new Error('Failed to import survey after multiple attempts');
      enhancedError.fileName = file.name;
      enhancedError.fileSize = file.size;
      enhancedError.originalError = finalError;
      throw enhancedError;
    });
  }
};

// Session API
export const getSessions = async (surveyId) => {
  const response = await api.get(`/sessions/survey/${surveyId}`);
  return response.data;
};

export const getSession = async (sessionId) => {
  try {
    console.log(`API call: Getting session with ID ${sessionId}`);
    const response = await api.get(`/sessions/${sessionId}`);
    
    if (!response || !response.data) {
      console.error('Empty response or missing data from session API call');
      throw new Error('Invalid response from server');
    }
    
    // Verify that the session contains a survey object
    if (!response.data.survey || !response.data.survey_id) {
      console.error('Session response is missing survey data:', response.data);
      throw new Error('Session data is invalid or incomplete');
    }
    
    console.log(`API call successful: Got session ${sessionId} with survey ${response.data.survey_id}`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error getting session ${sessionId}`, error);
    throw error; // Re-throw to let component handle it
  }
};

export const createSession = async (sessionData) => {
  try {
    console.log(`API call: Creating session for survey ${sessionData.survey_id}`);
    const response = await api.post('/sessions', sessionData);
    
    if (!response || !response.data) {
      console.error('Empty response or missing data from create session API call');
      throw new Error('Invalid response from server');
    }
    
    // Validate that the response includes an ID
    if (!response.data.id) {
      console.error('Session response is missing ID:', response.data);
      throw new Error('Session creation failed: ID not returned');
    }
    
    console.log(`API call successful: Created session ${response.data.id}`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error creating session`, error);
    throw error; // Re-throw to let component handle it
  }
};

export const submitAnswers = async (sessionId, answers) => {
  const response = await api.post(`/sessions/${sessionId}/answers`, answers);
  return response.data;
};

export const startRealtimeSession = async (sessionId) => {
  try {
    console.log(`API call: Starting realtime session with ID ${sessionId}`);
    const response = await api.get(`/sessions/${sessionId}/realtime`);
    
    if (!response || !response.data) {
      console.error('Empty response or missing data from realtime session API call');
      throw new Error('Invalid response from server');
    }
    
    if (response.data.status !== 'ok') {
      console.error('Realtime session status not OK:', response.data);
      throw new Error(response.data.message || 'Failed to start realtime session');
    }
    
    console.log(`API call successful: Realtime session started for ${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error starting realtime session ${sessionId}`, error);
    throw error; // Re-throw to let component handle it
  }
};

// Attachment API
export const getAttachments = async (sessionId) => {
  try {
    console.log(`API call: Getting attachments for session ${sessionId} with transcription info`);
    const response = await api.get(`/sessions/${sessionId}/attachments?include_transcription=true`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error getting attachments for session ${sessionId}`, error);
    throw error;
  }
};

export const uploadAttachment = async (sessionId, file) => {
  try {
    console.log(`API call: Uploading file (${(file.size / (1024 * 1024)).toFixed(2)}MB) to session ${sessionId}`);
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Create config with multipart headers and upload progress tracking
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${percentCompleted}%`);
      },
      // Increase timeout for large files (5 minutes)
      timeout: 5 * 60 * 1000
    };
    
    const response = await api.post(`/sessions/${sessionId}/attachments`, formData, config);
    
    if (!response || !response.data) {
      console.error('Empty response or missing data from upload API call');
      throw new Error('Invalid response from server');
    }
    
    console.log(`API call successful: File uploaded with ID ${response.data.id}`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error uploading file to session ${sessionId}`, error);
    throw error; // Re-throw to let component handle it
  }
};

export const processAttachments = async (sessionId) => {
  const response = await api.post(`/sessions/${sessionId}/process-attachments`);
  return response.data;
};

export const processSingleAttachment = async (attachmentId, forceReprocess = false) => {
  try {
    console.log(`API call: Processing attachment ${attachmentId}${forceReprocess ? ' (force reprocess)' : ''}`);
    
    // Construct the URL for the API endpoint - the attachments endpoints use the prefix /sessions
    // This is the correct URL format based on the Flask blueprints setup
    const url = forceReprocess 
      ? `/sessions/attachments/${attachmentId}/process?force=true` 
      : `/sessions/attachments/${attachmentId}/process`;
    
    console.log(`🔶 Full API URL: ${API_URL}${url}`);
    console.log(`🔶 API base URL: ${API_URL}`);
    console.log(`🔶 Attachment ID: ${attachmentId}`);
    
    // Make the API call with additional debugging
    console.log(`🔶 Making POST request to ${url} with forceReprocess=${forceReprocess}`);
    
    // Add a debug header to track this request specifically
    const headers = {
      'X-Force-Reprocess': forceReprocess ? 'true' : 'false',
      'X-Debug-Request': 'process-single-attachment',
      'Accept': 'application/json'
    };
    
    // Skip direct fetch due to Chrome extension interference, use axios directly
    console.log(`🔥 Using axios directly due to Chrome extension interference`);
    console.log(`🔥 Headers:`, headers);
    console.log(`🔥 URL:`, url);
    console.log(`🔥 Starting request at:`, new Date().toISOString());
    
    const response = await api.post(url, {}, { 
      headers,
      timeout: 60000 // 60 second timeout for this specific request
    });
    
    console.log(`🔥 Request completed at:`, new Date().toISOString());
    
    if (!response || !response.data) {
      console.error('Empty response or missing data from processing API call');
      throw new Error('Invalid response from server');
    }
    
    console.log(`API call successful: Processed attachment ${attachmentId}`, response.data);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error processing attachment ${attachmentId}`, error);
    
    // Enhanced error logging
    if (error.response) {
      // The request was made and the server responded with a status code that falls out of the range of 2xx
      console.error('Error response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Error request:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
    }
    
    // Properly structure the error to be handled at the component level
    const enhancedError = new Error(error.message || 'Failed to process attachment');
    enhancedError.response = error.response;
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

export const getAttachmentDetails = async (attachmentId, includeTranscription = false) => {
  const url = `/sessions/attachments/${attachmentId}?include_transcription=${includeTranscription ? 'true' : 'false'}`;
  const response = await api.get(url);
  return response.data;
};

// Task management API
export const getTaskStatus = async (taskId) => {
  try {
    console.log(`API call: Getting task status for task ${taskId}`);
    const response = await api.get(`/sessions/tasks/${taskId}`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error getting task status for task ${taskId}`, error);
    throw error;
  }
};

export const cancelTask = async (taskId) => {
  try {
    console.log(`API call: Cancelling task ${taskId}`);
    const response = await api.post(`/sessions/tasks/${taskId}/cancel`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error cancelling task ${taskId}`, error);
    throw error;
  }
};

export const abandonTask = async (taskId) => {
  try {
    console.log(`API call: Abandoning stale task ${taskId}`);
    const response = await api.post(`/sessions/tasks/${taskId}/abandon`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error abandoning task ${taskId}`, error);
    throw error;
  }
};

export const getAttachmentTasks = async (attachmentId) => {
  try {
    console.log(`API call: Getting tasks for attachment ${attachmentId}`);
    const response = await api.get(`/sessions/attachments/${attachmentId}/tasks`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error getting tasks for attachment ${attachmentId}`, error);
    throw error;
  }
};

export const retryAttachmentTask = async (attachmentId, force = false) => {
  try {
    console.log(`API call: Retrying transcription for attachment ${attachmentId} (force=${force})`);
    const url = force ? 
      `/sessions/attachments/${attachmentId}/retry?force=true` : 
      `/sessions/attachments/${attachmentId}/retry`;
    
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error retrying attachment ${attachmentId}`, error);
    throw error;
  }
};

export const fillAnswersFromAttachment = async (attachmentId) => {
  try {
    console.log(`API call: Filling answers from attachment ${attachmentId}`);
    const url = `/sessions/attachments/${attachmentId}/fill-answers`;
    
    // Add a debug header to track this request specifically
    const headers = {
      'X-Debug-Request': 'fill-answers'
    };
    
    // No timeout for answer filling - it can take a while
    const response = await api.post(url, {}, { 
      headers,
      timeout: 0 // No timeout
    });
    console.log(`API call successful: Filled answers from attachment ${attachmentId}`, response.data);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error filling answers from attachment ${attachmentId}`, error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

export const getSessionTasks = async (sessionId) => {
  try {
    console.log(`API call: Getting tasks for session ${sessionId}`);
    const response = await api.get(`/sessions/${sessionId}/tasks`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error getting tasks for session ${sessionId}`, error);
    throw error;
  }
};

export const getStaleTasks = async (timeoutSeconds = 300) => {
  try {
    console.log(`API call: Getting stale tasks with timeout ${timeoutSeconds}s`);
    const response = await api.get(`/sessions/tasks/stale?timeout=${timeoutSeconds}`);
    return response.data;
  } catch (error) {
    console.error(`API call failed: Error getting stale tasks`, error);
    throw error;
  }
};

export default {
  getSurveys,
  getSurvey,
  createSurvey,
  updateSurvey,
  deleteSurvey,
  importSurvey,
  getSessions,
  getSession,
  createSession,
  submitAnswers,
  startRealtimeSession,
  getAttachments,
  uploadAttachment,
  processAttachments,
  processSingleAttachment,
  getAttachmentDetails,
  getTaskStatus,
  cancelTask,
  abandonTask,
  getAttachmentTasks,
  retryAttachmentTask,
  fillAnswersFromAttachment,
  getSessionTasks,
  getStaleTasks
};
